import { useEffect, useState } from 'react';
import { useNavigation } from 'react-router-dom';
import { useTranslations } from '@/i18n/use-translations';

/**
 * Thin top-of-content progress bar shown while a route navigation (or
 * revalidation) is in flight, driven by React Router's `useNavigation()`
 * state. It gives instant feedback the moment a link is clicked — most
 * valuable on routes whose loaders still block (i.e. everything not yet
 * converted to the deferred-loader + skeleton pattern), and during the
 * one-off workspace-context warm-up on a cold navigation.
 *
 * A short exit delay lets the filled bar finish before it fades, so a quick
 * navigation still reads as a deliberate sweep rather than a flicker.
 */
export function NavigationProgressBar() {
  const navigation = useNavigation();
  const t = useTranslations('pages');
  const active = navigation.state !== 'idle';
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (active) {
      setVisible(true);

      return;
    }

    const timer = setTimeout(() => setVisible(false), 300);

    return () => clearTimeout(timer);
  }, [active]);

  if (!visible) return null;

  return (
    <div
      role="progressbar"
      aria-label={t('loading')}
      aria-busy={active}
      className="pointer-events-none absolute inset-x-0 top-0 z-50 h-0.5 overflow-hidden bg-transparent"
    >
      <div className="h-full w-1/3 rounded-full bg-interactive motion-safe:[animation:nav-progress_1s_ease-in-out_infinite]" />
    </div>
  );
}
